import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Grid,
  useToast,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useColorModeValue,
  useDisclosure,
  Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiPlus,
  FiEye,
  FiEdit,
  FiTrash,
  FiSend,
  FiCheck,
  FiX,
  FiMessageSquare,
  FiFilter,
  FiBriefcase,
  FiUsers,
  FiTrendingUp,
  FiClock,
} from "react-icons/fi";
import { jobRequisitionService } from "../jobRequisitionService";

const JobRequisitions = () => {
  const [requisitions, setRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRequisitions: 0,
    pendingApprovals: 0,
    approvedRequisitions: 0,
    rejectedRequisitions: 0,
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  // Modals
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalComments, setApprovalComments] = useState("");

  const {
    isOpen: isApprovalOpen,
    onOpen: onApprovalOpen,
    onClose: onApprovalClose,
  } = useDisclosure();

  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    fetchRequisitions();
    fetchStats();
  }, []);

  useEffect(() => {
    filterRequisitions();
  }, [
    requisitions,
    searchTerm,
    statusFilter,
    departmentFilter,
    priorityFilter,
  ]);

  const fetchRequisitions = async () => {
    try {
      setLoading(true);
      const response = await jobRequisitionService.getAllRequisitions();
      setRequisitions(response.requisitions || []);
    } catch (error) {
      console.error("Error fetching requisitions:", error);
      toast({
        title: "Error loading requisitions",
        description: "Could not load job requisitions",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const statsData = await jobRequisitionService.getRequisitionStats();

      const statusCounts = {};
      statsData.statusStats?.forEach((stat) => {
        statusCounts[stat._id] = stat.count;
      });

      setStats({
        totalRequisitions: Object.values(statusCounts).reduce(
          (a, b) => a + b,
          0,
        ),
        pendingApprovals: statusCounts["Pending Approval"] || 0,
        approvedRequisitions: statusCounts["Approved"] || 0,
        rejectedRequisitions: statusCounts["Rejected"] || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const filterRequisitions = () => {
    let filtered = requisitions;

    if (searchTerm) {
      filtered = filtered.filter(
        (req) =>
          req.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          req.requisitionId?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((req) => req.status === statusFilter);
    }

    if (departmentFilter) {
      filtered = filtered.filter((req) => req.department === departmentFilter);
    }

    if (priorityFilter) {
      filtered = filtered.filter((req) => req.priority === priorityFilter);
    }

    setFilteredRequisitions(filtered);
  };

  const handleCreateRequisition = () => {
    navigate("/recruiting/requisitions/create");
  };

  const handleEditRequisition = (requisition) => {
    navigate(`/recruiting/requisitions/${requisition._id}/edit`);
  };

  const handleViewRequisition = (requisition) => {
    navigate(`/recruiting/requisitions/${requisition._id}`);
  };

  const handleDeleteRequisition = async (requisition) => {
    if (
      !window.confirm(
        `Are you sure you want to delete requisition ${requisition.requisitionId}?`,
      )
    ) {
      return;
    }

    try {
      await jobRequisitionService.deleteRequisition(requisition._id);
      toast({
        title: "Requisition deleted",
        description: "Job requisition has been deleted successfully",
        status: "success",
        duration: 3000,
      });
      fetchRequisitions();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error deleting requisition",
        description: "Could not delete the requisition",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleSubmitForApproval = async (requisition) => {
    try {
      await jobRequisitionService.submitForApproval(requisition._id);
      toast({
        title: "Submitted for approval",
        description: "Requisition has been submitted for approval",
        status: "success",
        duration: 3000,
      });
      fetchRequisitions();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error submitting for approval",
        description: "Could not submit the requisition",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleApprovalAction = (requisition, action) => {
    setSelectedRequisition(requisition);
    setApprovalAction(action);
    setApprovalComments("");
    onApprovalOpen();
  };

  const handleProcessApproval = async () => {
    try {
      await jobRequisitionService.processApproval(
        selectedRequisition._id,
        approvalAction,
        approvalComments,
      );

      toast({
        title: `Requisition ${approvalAction}d`,
        description: `Job requisition has been ${approvalAction}d successfully`,
        status: "success",
        duration: 3000,
      });

      fetchRequisitions();
      fetchStats();
      onApprovalClose();
    } catch (error) {
      toast({
        title: `Error ${approvalAction}ing requisition`,
        description: "Could not process the approval",
        status: "error",
        duration: 3000,
      });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Draft":
        return "gray";
      case "Pending Approval":
        return "yellow";
      case "Approved":
        return "green";
      case "Rejected":
        return "red";
      case "On Hold":
        return "orange";
      case "Cancelled":
        return "red";
      case "Filled":
        return "blue";
      default:
        return "gray";
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "Low":
        return "gray";
      case "Medium":
        return "blue";
      case "High":
        return "orange";
      case "Critical":
        return "red";
      default:
        return "gray";
    }
  };

  const getDepartments = () => {
    return [
      ...new Set(requisitions.map((req) => req.department).filter(Boolean)),
    ];
  };

  const canEditRequisition = (requisition) => {
    return requisition.status === "Draft";
  };

  const canDeleteRequisition = (requisition) => {
    return requisition.status === "Draft";
  };

  const canSubmitForApproval = (requisition) => {
    return requisition.status === "Draft";
  };

  const canApproveRequisition = (requisition) => {
    return (
      requisition.status === "Pending Approval" &&
      requisition.approvals?.some((approval) => approval.status === "Pending")
    );
  };

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.700");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const rowHoverBg = useColorModeValue("gray.50", "gray.750");

  if (loading) {
    return (
      <Flex h="60vh" align="center" justify="center" direction="column" gap={3}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
        <Text fontSize="sm" color={subtleText}>
          Loading requisitions…
        </Text>
      </Flex>
    );
  }

  /* ── Row actions (shared between table + cards) ── */
  const RowActions = ({ requisition }) => (
    <HStack spacing={0}>
      <IconButton
        icon={<FiEye />}
        size="sm"
        variant="ghost"
        aria-label="View"
        onClick={() => handleViewRequisition(requisition)}
      />
      {canEditRequisition(requisition) && (
        <IconButton
          icon={<FiEdit />}
          size="sm"
          variant="ghost"
          aria-label="Edit"
          onClick={() => handleEditRequisition(requisition)}
        />
      )}
      {canSubmitForApproval(requisition) && (
        <IconButton
          icon={<FiSend />}
          size="sm"
          variant="ghost"
          colorScheme="blue"
          aria-label="Submit for approval"
          onClick={() => handleSubmitForApproval(requisition)}
        />
      )}
      {canApproveRequisition(requisition) && (
        <>
          <IconButton
            icon={<FiCheck />}
            size="sm"
            variant="ghost"
            colorScheme="green"
            aria-label="Approve"
            onClick={() => handleApprovalAction(requisition, "approve")}
          />
          <IconButton
            icon={<FiX />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            aria-label="Reject"
            onClick={() => handleApprovalAction(requisition, "reject")}
          />
        </>
      )}
      {canDeleteRequisition(requisition) && (
        <IconButton
          icon={<FiTrash />}
          size="sm"
          variant="ghost"
          colorScheme="red"
          aria-label="Delete"
          onClick={() => handleDeleteRequisition(requisition)}
        />
      )}
    </HStack>
  );

  return (
    <Box minH="100vh" bg={bg} px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
      <VStack spacing={5} align="stretch">
        {/* ── Header ── */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "flex-start", sm: "center" }}
          gap={3}
        >
          <Box>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="700"
              letterSpacing="-0.02em"
            >
              Job Requisitions
            </Text>
            <Text fontSize="sm" color={subtleText} mt={0.5}>
              Manage hiring requests and approval workflows
            </Text>
          </Box>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            size="sm"
            flexShrink={0}
            onClick={handleCreateRequisition}
          >
            Create Requisition
          </Button>
        </Flex>

        {/* ── Stats ── */}
        <Grid
          templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
          gap={3}
        >
          {[
            { label: "Total", value: stats.totalRequisitions },
            { label: "Pending", value: stats.pendingApprovals },
            { label: "Approved", value: stats.approvedRequisitions },
            { label: "Rejected", value: stats.rejectedRequisitions },
          ].map(({ label, value }) => (
            <Box
              key={label}
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              px={4}
              py={3}
            >
              <Text
                fontSize="xs"
                color={subtleText}
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="600"
              >
                {label}
              </Text>
              <Text fontSize="2xl" fontWeight="700" mt={1}>
                {value}
              </Text>
            </Box>
          ))}
        </Grid>

        {/* ── Filters ── */}
        <Box
          bg={cardBg}
          border="1px solid"
          borderColor={border}
          borderRadius="xl"
          p={4}
        >
          <VStack spacing={3} align="stretch">
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search by title, department, or ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>
            <Grid
              templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }}
              gap={3}
            >
              <Select
                size="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                borderRadius="md"
              >
                <option value="">All Status</option>
                <option value="Draft">Draft</option>
                <option value="Pending Approval">Pending Approval</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="On Hold">On Hold</option>
                <option value="Cancelled">Cancelled</option>
                <option value="Filled">Filled</option>
              </Select>
              <Select
                size="sm"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                borderRadius="md"
              >
                <option value="">All Departments</option>
                {getDepartments().map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                borderRadius="md"
              >
                <option value="">All Priorities</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FiFilter />}
                onClick={() => {
                  setSearchTerm("");
                  setStatusFilter("");
                  setDepartmentFilter("");
                  setPriorityFilter("");
                }}
              >
                Clear
              </Button>
            </Grid>
          </VStack>
        </Box>

        {/* ── Table (md+) ── */}
        <Box
          display={{ base: "none", md: "block" }}
          bg={cardBg}
          border="1px solid"
          borderColor={border}
          borderRadius="xl"
          overflow="hidden"
        >
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th py={3} fontSize="xs">
                  Requisition
                </Th>
                <Th py={3} fontSize="xs">
                  Job Title
                </Th>
                <Th py={3} fontSize="xs">
                  Department
                </Th>
                <Th py={3} fontSize="xs">
                  Priority
                </Th>
                <Th py={3} fontSize="xs">
                  Status
                </Th>
                <Th py={3} fontSize="xs">
                  Created
                </Th>
                <Th py={3} fontSize="xs"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredRequisitions.map((req) => (
                <Tr
                  key={req._id}
                  _hover={{ bg: rowHoverBg }}
                  cursor="pointer"
                  onClick={() => handleViewRequisition(req)}
                >
                  <Td py={3}>
                    <Text fontWeight="500" fontSize="sm">
                      {req.requisitionId}
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      {req.numberOfPositions} position
                      {req.numberOfPositions > 1 ? "s" : ""}
                    </Text>
                  </Td>
                  <Td py={3}>
                    <Text fontWeight="500" fontSize="sm">
                      {req.jobTitle}
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      {req.employmentType} · {req.jobLevel}
                    </Text>
                  </Td>
                  <Td py={3}>
                    <Text fontSize="sm">{req.department}</Text>
                  </Td>
                  <Td py={3}>
                    <Badge
                      colorScheme={getPriorityBadgeColor(req.priority)}
                      variant="subtle"
                      fontSize="xs"
                    >
                      {req.priority}
                    </Badge>
                  </Td>
                  <Td py={3}>
                    <Badge
                      colorScheme={getStatusBadgeColor(req.status)}
                      variant="subtle"
                      fontSize="xs"
                    >
                      {req.status}
                    </Badge>
                  </Td>
                  <Td py={3}>
                    <Text fontSize="xs">
                      {new Date(req.createdAt).toLocaleDateString()}
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      by {req.createdByName}
                    </Text>
                  </Td>
                  <Td py={3} onClick={(e) => e.stopPropagation()}>
                    <RowActions requisition={req} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {filteredRequisitions.length === 0 && (
            <Box textAlign="center" py={10}>
              <Text fontSize="sm" color={subtleText}>
                {requisitions.length === 0
                  ? "No job requisitions found"
                  : "No requisitions match your filters"}
              </Text>
            </Box>
          )}
        </Box>

        {/* ── Card list (mobile) ── */}
        <VStack
          display={{ base: "flex", md: "none" }}
          spacing={3}
          align="stretch"
        >
          {filteredRequisitions.length === 0 ? (
            <Box
              bg={cardBg}
              border="1px solid"
              borderColor={border}
              borderRadius="xl"
              py={10}
              textAlign="center"
            >
              <Text fontSize="sm" color={subtleText}>
                {requisitions.length === 0
                  ? "No job requisitions found"
                  : "No requisitions match your filters"}
              </Text>
            </Box>
          ) : (
            filteredRequisitions.map((req) => (
              <Box
                key={req._id}
                bg={cardBg}
                border="1px solid"
                borderColor={border}
                borderRadius="xl"
                p={4}
                onClick={() => handleViewRequisition(req)}
                cursor="pointer"
                _active={{ opacity: 0.85 }}
              >
                <Flex justify="space-between" align="flex-start" mb={2}>
                  <Box flex={1} mr={3}>
                    <Text fontWeight="600" fontSize="sm" noOfLines={1}>
                      {req.jobTitle}
                    </Text>
                    <Text fontSize="xs" color={subtleText} mt={0.5}>
                      {req.requisitionId} · {req.department}
                    </Text>
                  </Box>
                  <Badge
                    colorScheme={getStatusBadgeColor(req.status)}
                    variant="subtle"
                    fontSize="xs"
                    flexShrink={0}
                  >
                    {req.status}
                  </Badge>
                </Flex>
                <HStack spacing={2} mb={3} flexWrap="wrap">
                  <Badge
                    colorScheme={getPriorityBadgeColor(req.priority)}
                    variant="outline"
                    fontSize="xs"
                  >
                    {req.priority}
                  </Badge>
                  <Text fontSize="xs" color={subtleText}>
                    {req.employmentType}
                  </Text>
                  <Text fontSize="xs" color={subtleText}>
                    ·
                  </Text>
                  <Text fontSize="xs" color={subtleText}>
                    {req.jobLevel}
                  </Text>
                  <Text fontSize="xs" color={subtleText}>
                    ·
                  </Text>
                  <Text fontSize="xs" color={subtleText}>
                    {req.numberOfPositions} position
                    {req.numberOfPositions > 1 ? "s" : ""}
                  </Text>
                </HStack>
                <Flex justify="space-between" align="center">
                  <Text fontSize="xs" color={subtleText}>
                    {new Date(req.createdAt).toLocaleDateString()} ·{" "}
                    {req.createdByName}
                  </Text>
                  <Box onClick={(e) => e.stopPropagation()}>
                    <RowActions requisition={req} />
                  </Box>
                </Flex>
              </Box>
            ))
          )}
        </VStack>
      </VStack>

      {/* Approval Modal */}
      <Modal isOpen={isApprovalOpen} onClose={onApprovalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {approvalAction === "approve" ? "Approve" : "Reject"} Requisition
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Are you sure you want to {approvalAction} the requisition{" "}
                <strong>{selectedRequisition?.requisitionId}</strong> for{" "}
                <strong>{selectedRequisition?.jobTitle}</strong>?
              </Text>

              <FormControl>
                <FormLabel>Comments (Optional)</FormLabel>
                <Textarea
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  placeholder="Add any comments..."
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onApprovalClose}>
              Cancel
            </Button>
            <Button
              colorScheme={approvalAction === "approve" ? "green" : "red"}
              onClick={handleProcessApproval}
              ml={3}
            >
              {approvalAction === "approve" ? "Approve" : "Reject"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default JobRequisitions;
